import { z } from "zod";
import { CF_DATE_MAX_TODAY, isValidYmd } from "../utils/cfValueTypes";

/* ------------------------------------------------------------------ */
/* CUSTOM INPUT TYPES */
/* ------------------------------------------------------------------ */

export enum SpesificTypes {
  Normal = "Normal",
  Location = "Location",
  Intake = "Intake",
  Country = "Country",
  Tags = "Tags",
}

export enum CustomInputTypeEnums {
  SELECTOR = "SELECTOR",
  REMARK = "REMARK",
  TEXT_FIELD = "TEXT_FIELD",
  NUMBERS = "NUMBERS",
  PERCENTAGE = "PERCENTAGE",
  DATE = "DATE",
}
/** Tuple source of truth */
export const CUSTOM_INPUT_TYPE_KEYS = [
  "SELECTOR",
  "REMARK",
  "TEXT_FIELD",
  // Text-valued with a format — see utils/cfValueTypes.ts.
  "NUMBERS",
  "PERCENTAGE",
  "DATE",
  "VALUE",
  "MODULE",
] as const;

/** Union type */
export type CustomInputType = (typeof CUSTOM_INPUT_TYPE_KEYS)[number];

/** UI labels */
export const CUSTOM_INPUT_TYPES: {
  key: CustomInputType;
  label: string;
}[] = [
  { key: "SELECTOR", label: "Dropdown / Selector" },
  { key: "REMARK", label: "Remark / Note" },
  { key: "TEXT_FIELD", label: "Text Field" },
  { key: "NUMBERS", label: "Number" },
  { key: "PERCENTAGE", label: "Percentage" },
  { key: "DATE", label: "Date" },
  { key: "VALUE", label: "Value / Number" },
  { key: "MODULE", label: "Module Reference" },
];

/* ------------------------------------------------------------------ */
/* MODULE INPUT TYPES */
/* ------------------------------------------------------------------ */

export interface ModuleOption {
  key: string;
  label: string;
}

const CORE_MODULES: ModuleOption[] = [
  { key: "child", label: "Choice(Deals)" },
  { key: "leads", label: "Leads" },
  { key: "user", label: "User(attach to users)" },
];

const INDUSTRY_NICHE_MODULES: Record<number, ModuleOption[]> = {
  1: [
    { key: "university", label: "University" },
    { key: "course", label: "Course" },
    { key: "qualificationScore", label: "Qualification Score" },
  ],
  4: [
    { key: "videoService", label: "Service" },
    { key: "videoServiceVarients", label: "Service Variant" },
    { key: "videoModels", label: "Model" },
    { key: "videoAmbient", label: "Ambient" },
  ],
  5: [
    { key: "academyProgram", label: "Program" },
    { key: "academyProgramVariant", label: "Program Variant" },
  ],
};

export function getModuleInputTypes(
  industryId: number | undefined
): ModuleOption[] {
  const niche = INDUSTRY_NICHE_MODULES[industryId ?? 1] ?? [];
  return [...niche, ...CORE_MODULES];
}

const ALL_MODULE_LABELS: Record<string, string> = {
  university: "University",
  course: "Course",
  qualificationScore: "Qualification Score",
  videoService: "Service",
  videoServiceVarients: "Service Variant",
  videoModels: "Model",
  videoAmbient: "Ambient",
  academyProgram: "Program",
  academyProgramVariant: "Program Variant",
  child: "Choice(Deals)",
  leads: "Leads",
  user: "User",
};

export function getModuleLabel(key: string): string {
  return ALL_MODULE_LABELS[key] ?? key;
}

/** @deprecated – use getModuleInputTypes(industryId) instead */
export const MODULE_INPUT_TYPES = getModuleInputTypes(1);

/* ------------------------------------------------------------------ */
/* SCHEMA */
/* ------------------------------------------------------------------ */

/**
 * SELECTOR option as sent to / received from the server.
 *
 * Existing options carry an `id` so the backend can `update` the row in
 * place when the value is renamed (preserving the row id and every
 * `CustomInputItemScope` that points at it). Brand-new options have no
 * id; the server creates a row. Strings are accepted for legacy callers
 * and treated as "new option with this value".
 */
export const CustomInputOptionSchema = z.union([
  z.string(),
  z.object({
    id: z.number().optional(),
    value: z.string(),
    /** Named OPTION group — options sharing a group name render together
     *  under that heading in filter dropdowns. null/absent = ungrouped. */
    filterGroup: z.string().nullable().optional(),
  }),
]);
export type CustomInputOption = z.infer<typeof CustomInputOptionSchema>;

/** Extract the display label from either string or object option. */
export function optionValue(opt: CustomInputOption): string {
  return typeof opt === "string" ? opt : opt.value;
}

export const CustomInputSchema = z
  .object({
    name: z.string().min(2, "Field name is required"),

    /**
     * Display name shown across the CRM instead of `name` when set.
     * Empty = fall back to `name`. `name` stays the machine identity
     * (Meta sync overwrites it on auto-created fields).
     */
    label: z.string().trim().max(100, "Label is too long").optional(),

    type: z.enum(CUSTOM_INPUT_TYPE_KEYS),

    /**
     * SELECTOR only — how many options may be picked, everywhere the field
     * renders. "normal" = single select, "tags" = multi select. Mirrors
     * `CustomInput.valueTypes`; the legacy third value ("img") is never
     * written from this form and reads as single.
     */
    valueTypes: z.enum(["normal", "tags"]),

    // SELECTOR — each entry is `{ id?, value }` (existing) or a string
    // (brand new); `id` is the only signal the server uses to detect a
    // rename vs an add.
    options: z.array(CustomInputOptionSchema).optional(),

    // MODULE
    modules: z.array(z.string()).optional(),

    // FIELD BEHAVIOUR
    isMandatory: z.boolean(),
    isQuickAddFilters: z.boolean(),
    isNeedPermission: z.boolean(),
    /**
     * SELECTOR only: surface this field as a global filter pill on the
     * Dashboard and in Reports (sends the same cf_<id> params as the Leads
     * page).
     */
    isGlobalFilter: z.boolean(),
    /**
     * SELECTOR only: a KEY FIELD (e.g. "Team") — counted by everywhere Country
     * is: its own Dashboard card, a Reports tab, the Segregation pivot, the
     * Leads-page picker (`useKeyFields`).
     */
    isKeyField: z.boolean(),
    /**
     * Hard = restricts assigning AND the view (LeadScope/ChildScope filter).
     * Low  = never blocks a person (owner rule 2026-10-08): users still see
     *        every lead/deal and anyone can be assigned by hand; the tie only
     *        steers automatic distribution (automations still honour it).
     * Default lives on the form (`defaultValues`) and in edit-mode hydration —
     * keeping `.default()` off the schema avoids the input/output type drift
     * that breaks `zodResolver` inference.
     */
    accessControlMode: z.enum(["LOW", "HARD"]),
    /**
     * DEPRECATED, never shown or validated: the "free option" is gone — a
     * gated field has no universally-accessible value (owner rule
     * 2026-10-07). The server ignores it. Kept so older payload shapes parse.
     */
    defaultAccessOptionValue: z.string().optional(),
    spesificTypes: z.string().optional(),

    // STAGE VISIBILITY
    stageId: z.number().nullable().optional(),

    /**
     * DATE only — the latest day a value may be: null = any date, "today" =
     * no future dates, or a "YYYY-MM-DD" day. The limit section writes
     * "pick" while "Up to a date" has no day yet, which the refine below
     * turns into an error. Ignored (stored NULL) for every other type.
     */
    dateMax: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.type === "DATE" &&
      data.dateMax &&
      data.dateMax !== CF_DATE_MAX_TODAY &&
      !isValidYmd(data.dateMax)
    ) {
      ctx.addIssue({
        path: ["dateMax"],
        message: "Pick the latest allowed date",
        code: z.ZodIssueCode.custom,
      });
    }

    if (data.type === "SELECTOR" && !data.options?.length) {
      ctx.addIssue({
        path: ["options"],
        message: "At least one option is required",
        code: z.ZodIssueCode.custom,
      });
    }

    if (data.type === "MODULE" && !data.modules?.length) {
      ctx.addIssue({
        path: ["modules"],
        message: "Select at least one module",
        code: z.ZodIssueCode.custom,
      });
    }
  });

/* ------------------------------------------------------------------ */
/* FINAL FORM TYPE */
/* ------------------------------------------------------------------ */

export type CustomInputFormData = z.infer<typeof CustomInputSchema>;
